import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Close, Send } from "@mui/icons-material";
import ApiService from "../../../config/api";
import Sidebar from "../components/Sidebar";
import AdminNavbar from "../components/AdminNavbar";
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const CreateAndEditLocality = () => {
  const navigate = useNavigate();
  const { state } = useLocation(); 
  const isEdit = state?.isEdit || false;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const toggleDrawer = () => setSidebarOpen(!sidebarOpen);


  const [name, setName] = useState(state?.name || "");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(state?.image || null);

  // ✅ Handle Image Upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  // ✅ Remove Image
  const handleRemoveImage = () => {
    setImage(null);
    setPreview(null);
  };

  // ✅ Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Locality name is required");
      return;
    }
    if (!preview) {
      toast.error("Please select an image");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", name);
      if (image) {
        formData.append("image", image); // send file
      }

      if (isEdit) {
        await ApiService.put(`localities/edit/${state.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Locality updated successfully!");
      } else {
        await ApiService.post("localities/create", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Locality created successfully!");
      }

      navigate("/admin/localities");
    } catch (err) {
      console.error("Error saving locality:", err);
      toast.error("Something went wrong, please try again!");
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        backgroundColor: 'var(--bs-secondary)',
        minHeight: '100vh',
      }}
    >
      <Sidebar open={sidebarOpen} toggleDrawer={toggleDrawer} setOpen={setSidebarOpen} />
      <Box>
        <AdminNavbar />
         <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "var(--bs-primary)",
              textTransform: "none",
              fontWeight: 500,
              padding:2
            }}
          >
            Back to Localities
          </Button>


        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper sx={{mt:3, p: 3, width: 600, borderRadius: 3, boxShadow: 3, backgroundColor: 'var(--bs-primary)' }}>
            <Typography
              variant="h5"
              sx={{ mb: 2, color: "var(--bs-accent)", textAlign: "center" }}
            >
              {isEdit ? "Edit Locality" : "Create Locality"}
            </Typography>

            <form onSubmit={handleSubmit}>
              {/* Locality Name */}
              <TextField
                label="Locality Name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                InputProps={{
    sx: {
      color: 'var(--bs-accent)', // text inside input
    }
  }}
  InputLabelProps={{
    sx: {
      color: 'var(--bs-accent)', // label color
      '&.Mui-focused': {
        color: 'var(--bs-accent)',
      },
    }
  }}
  sx={{
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'var(--bs-accent)',
      },
      '&:hover fieldset': {
        borderColor: 'var(--bs-accent)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--bs-accent)',
      },
    },
  }}   
              />

              {/* Image Upload */}
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ marginY: 5 ,color: 'var(--bs-accent)', borderColor: 'var(--bs-accent)'}}
              >
                {image ? "Change Image" : "Upload Image"}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>

              {/* Preview */}
              {preview && (
                <Box
                  sx={{
                    position: "relative",
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={preview}
                    alt="preview"
                    sx={{
                      width: "100%",
                      height: 150,
                      objectFit: "contain",
                      borderRadius: 2,
                      border: "1px solid #ccc",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "white",
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}


              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>

                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/localities')}
                  sx={{ color: 'var(--bs-accent)', borderColor: 'var(--bs-secondary)' }}
                >
                  Cancel
                </Button>

                {/* Submit */}
                <Button
                  type="submit"
                  variant="contained"
endIcon={ <Send />}
sx={{
                  backgroundColor: 'var(--bs-accent)',
                  color: 'var(--bs-primary)',
                  '&:hover': { backgroundColor: 'var(--bs-secondary)' },
                }} 
                >
                  {isEdit ? "Update" : "Create"}
                </Button>

              </Box>

            </form>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateAndEditLocality;
